import { Injectable, inject } from '@angular/core';
import { map } from 'rxjs/operators';

import {
  PROGRESS_PAYMENT_NESTED_TABLE_PAGE_SIZE,
  ProgressPaymentItemTable,
  ProgressPaymentItemsPagesResponse,
  ProgressPaymentModulesResponse,
  ProgressPaymentRequestParams,
} from '@models/api';
import { FieldsEntityInstanceResponse, SaveTransitionScreenBody } from '@models/api/module-instance.model';
import { KeyValuePeriodModel, KeyValueType } from '@models/common';
import { ListResponse } from '@models/helpers';
import {
  KeyValueRevisionsModel,
  ProgressPaymentCreateParams,
  ProgressPaymentPeriod,
} from '@module-instance/progress-payment/common';
import { HttpService } from '@shared/services';

@Injectable({
  providedIn: 'root',
})
export class ProgressPaymentService {
  private http = inject(HttpService);

  getPpPeriod(entityInstanceId: number) {
    const params = this.http.buildHttpParams({ entityInstanceId });
    return this.http.get<ProgressPaymentPeriod>(`_api/PaymentPeriod/GetFromEntityInstance`, {
      httpOptions: { params },
      useLoader: false,
    });
  }

  getPpPeriods(contractId: number) {
    const params = this.http.buildHttpParams({ contractId });
    return this.http.get<KeyValuePeriodModel<number, string>[]>('_api/PaymentPeriod/Dict', {
      httpOptions: { params },
      useLoader: false,
    });
  }

  getFieldsEntityInstance(
    entityInstanceId: number,
    contractId: number,
    workflowId: number,
    transitionId?: number,
    screenId?: number,
  ) {
    const params = this.http.buildHttpParams({ entityInstanceId, contractId, workflowId, transitionId, screenId });
    return this.http.post<FieldsEntityInstanceResponse>('_api/ProgressPayment/GetFieldsEntityInstance', null, {
      useLoader: false,
      httpOptions: { params },
    });
  }

  create(body: SaveTransitionScreenBody, params: ProgressPaymentCreateParams) {
    const httpParams = this.http.buildHttpParams({
      workflowId: params.workflowId,
      contractId: params.contractId,
      paymentPeriodId: params.paymentPeriodId,
      moduleTypeId: params.moduleTypeId,
      roleId: params.roleId,
      screenId: params.screenId,
      sovEntityInstanceId: params.sovEntityInstanceId,
    });
    return this.http.post<number>('_api/ProgressPayment/ProgressPaymentCreate', body, {
      useLoader: false,
      httpOptions: { params: httpParams },
    });
  }

  getAvailableSOVs(contractEntityInstanceId: number) {
    const params = this.http.buildHttpParams({ contractEntityInstanceId });
    return this.http.get<KeyValueRevisionsModel[]>('_api/ProgressPayment/GetAvailableSOVs', {
      httpOptions: { params },
      useLoader: false,
    });
  }

  getPeriodsBySov(sovEntityInstanceId: number, contractId: number) {
    const params = this.http.buildHttpParams({ sovEntityInstanceId, contractId });
    return this.http.get<KeyValueType[]>('_api/PaymentPeriod/DictFilterBySOV', {
      httpOptions: { params },
      useLoader: false,
    });
  }

  getProgressPaymentModules({
    contractId,
    entityInstanceId: progressPaymentEntityInstanceId,
  }: Pick<ProgressPaymentRequestParams, 'contractId' | 'entityInstanceId'>) {
    const httpParams = this.http.buildHttpParams({ contractId, progressPaymentEntityInstanceId, editMode: false });
    return this.http.post<ProgressPaymentModulesResponse>(
      '_api/ProgressPayment/ProgressPaymentModules',
      {},
      {
        useLoader: false,
        httpOptions: { params: httpParams },
      },
    );
  }

  getProgressPaymentItemsPages({
    contractId,
    entityInstanceId: progressPaymentEntityInstanceId,
    moduleId,
  }: Pick<ProgressPaymentRequestParams, 'contractId' | 'entityInstanceId' | 'moduleId'>) {
    const httpParams = this.http.buildHttpParams({ contractId, progressPaymentEntityInstanceId, moduleId });
    const body = {
      criteria: '',
      isCurrent: false,
      /* The users should not be able to change the page size */
      pagesize: PROGRESS_PAYMENT_NESTED_TABLE_PAGE_SIZE,
    };
    return this.http.post<ProgressPaymentItemsPagesResponse>(
      '_api/ProgressPayment/GetProgressPaymentItemsPages',
      body,
      {
        useLoader: false,
        httpOptions: { params: httpParams },
      },
    );
  }

  getProgressPaymentItems(
    {
      contractId,
      entityInstanceId: progressPaymentEntityInstanceId,
      moduleId,
    }: Pick<ProgressPaymentRequestParams, 'contractId' | 'entityInstanceId' | 'moduleId'>,
    offset: number,
  ) {
    const httpParams = this.http.buildHttpParams({ contractId, progressPaymentEntityInstanceId, moduleId });
    const body = {
      criteria: '',
      isCurrent: false,
      page: offset,
      pagesize: PROGRESS_PAYMENT_NESTED_TABLE_PAGE_SIZE,
    };
    return this.http
      .post<ListResponse<ProgressPaymentItemTable>>('_api/ProgressPayment/GetProgressPaymentItems', body, {
        useLoader: false,
        httpOptions: { params: httpParams },
      })
      .pipe(map(({ data }) => data));
  }
}
